import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import isNil from 'lodash.isnil';
import isEmpty from 'lodash.isempty';
import {
    RESCORING_INSTRUCTION_TYPES,
    LEGEND_SECTION_EDIT_MODE,
} from '../../../../../../constants/reading/TroConstants';
import AudioPlayer from '../../../../../../components/audioPlayer/AudioPlayer';
import Modal from '../../../../../../components/modal/Modal';
import Button from '../../../../../../components/button/Button';
import RescoringLegend from '../rescoringLegend/RescoringLegend';
import TroBookContent from '../troBookContent/TroBookContent';
import RescoringInstruction from './rescoringInstruction/RescoringInstruction';
import {
    getEventPosition,
    getTroBannerContent,
    getUserEventsWithFormatting,
    isSaveButtonDisabled,
    getToolbarType,
    getEndWordData,
} from '../../../../../../utils/reading/troUtils';
import './RescoringEditMode.scss';
import {
    onEscHandler,
    getToolbarPosition,
} from '../../../../../../components/toolbar/toolbarUtils';
import { TOOLBAR_TYPES } from '../../../../../../constants/shared/ToolbarConstants';
import { getOmitMark, getSubstituteMark } from '../../../../../../utils/shared/marksBuilderUtils';
import usePressHold from '../../../../../../hooks/PressHoldClicker';
import { getTroMarksClassNames } from '../../../../../../utils/reading/marksBuilderUtils';
import RescoringToolbars from './RescoringToolbars';

const { COMMON_INSTRUCTION, NOT_SCORED_INSTRUCTION } = RESCORING_INSTRUCTION_TYPES;
const { INSERT_TOOLBAR, TOOLBAR, END_TIME_TOOLBAR } = TOOLBAR_TYPES;

const RescoringEditMode = ({
    bookId,
    listOfSentences,
    bookInformation,
    vrrResult,
    isEditMode,
    setEditMode,
    closeConfirmationPopup,
    patchBookContentRemarks,
    audioLookerEvent,
}) => {
    const initialTroRescoringData = useMemo(
        () => ({
            bookId: '',
            endPosition: null,
            endTime: null,
            isSaveButtonDisabled: true,
        }),
        [],
    );
    const [troRescoringInfo, setTroRescoringInfo] = useState({
        troRescoringData: initialTroRescoringData,
        toolbarConfiguration: {
            toolbarType: null,
        },
    });
    const [sentences, setSentences] = useState([...listOfSentences]);

    const { audioSrc, isUntranscribable, startPosition, endPosition, audioDuration } = vrrResult;
    const { bookTitle, complexityLevel } = bookInformation;

    useEffect(() => {
        setTroRescoringInfo(troRescoringInfoState => ({
            ...troRescoringInfoState,
            troRescoringData: {
                ...initialTroRescoringData,
                bookId,
            },
        }));
    }, [bookId, initialTroRescoringData]);

    const cancelEditMode = () => {
        setTroRescoringInfo({ ...troRescoringInfo, troRescoringData: initialTroRescoringData });
        setEditMode(false);
    };

    const setEndWordData = (endWordPosition, endWordTime) =>
        setTroRescoringInfo({
            ...troRescoringInfo,
            troRescoringData: {
                ...troRescoringInfo.troRescoringData,
                ...getEndWordData(isUntranscribable, endWordPosition, endWordTime),
            },
            toolbarConfiguration: {
                ...troRescoringInfo.toolbarConfiguration,
                toolbarType: getToolbarType(
                    endWordPosition,
                    endWordTime,
                    troRescoringInfo.toolbarConfiguration.toolbarType,
                ),
            },
        });

    const closeToolbarHandler = () => {
        if (troRescoringInfo.toolbarConfiguration.toolbarType === END_TIME_TOOLBAR) {
            setEndWordData(null, null);
        } else {
            setTroRescoringInfo({
                ...troRescoringInfo,
                toolbarConfiguration: {
                    ...troRescoringInfo.toolbarConfiguration,
                    toolbarType: null,
                },
            });
        }
    };

    const setWordEvent = wordEvent => {
        const wordPosition = getEventPosition(wordEvent.eventDetails);

        sentences[wordPosition] = {
            ...sentences[wordPosition],
            marks: [...get(sentences[wordPosition], 'marks', []), wordEvent],
        };

        setTroRescoringInfo({
            ...troRescoringInfo,
            toolbarConfiguration: {
                ...troRescoringInfo.toolbarConfiguration,
                toolbarType: null,
            },
            troRescoringData: {
                ...troRescoringInfo.troRescoringData,
                isSaveButtonDisabled: isSaveButtonDisabled(
                    listOfSentences,
                    sentences,
                    troRescoringInfo.troRescoringData.endTime,
                ),
            },
        });

        setSentences(sentences);
    };

    const saveEditMode = () => {
        const mergedRescoringEndPosition = isNil(troRescoringInfo.troRescoringData.endPosition)
            ? endPosition
            : troRescoringInfo.troRescoringData.endPosition;
        const data = {
            book_id: troRescoringInfo.troRescoringData.bookId,
            end_position: troRescoringInfo.troRescoringData.endPosition,
            end_time: troRescoringInfo.troRescoringData.endTime,
            events: getUserEventsWithFormatting(sentences, mergedRescoringEndPosition),
        };
        patchBookContentRemarks(data);
        setEditMode(false);
        closeConfirmationPopup();
    };

    const onClickHandler = (
        isInsert,
        isEndTime,
        wordReference,
        contentReference,
        marks,
        activeWordPosition,
    ) => {
        const toolbarTypeByInsert = isInsert ? INSERT_TOOLBAR : TOOLBAR;
        const finalToolbarType = isEndTime ? END_TIME_TOOLBAR : toolbarTypeByInsert;
        const toolbarPosition = getToolbarPosition(false, wordReference, contentReference);

        const omitMark = getOmitMark(marks);
        const substituteMark = getSubstituteMark(marks);
        const corrections = [!isEmpty(omitMark), !isEmpty(substituteMark), isInsert];

        const toolbarCommonProps = {
            corrections,
            activeWordPosition,
            toolbarPosition,
            toolbarType: finalToolbarType,
            closeToolbar: closeToolbarHandler,
        };

        setTroRescoringInfo({ ...troRescoringInfo, toolbarConfiguration: toolbarCommonProps });
    };

    const onPressHoldHandler = (wordRef, contentReference, marks, activeWordPosition) =>
        isNil(endPosition)
            ? usePressHold(() =>
                  onClickHandler(false, true, wordRef, contentReference, marks, activeWordPosition),
              )
            : {};

    const onKeyDownHandler = ({ keyCode }) => {
        onEscHandler(
            keyCode,
            troRescoringInfo.toolbarConfiguration.toolbarType,
            closeToolbarHandler,
        );
    };
    const isInsertToolbar = troRescoringInfo.toolbarConfiguration.toolbarType === INSERT_TOOLBAR;
    const isEndTimeToolbar = troRescoringInfo.toolbarConfiguration.toolbarType === END_TIME_TOOLBAR;
    const isCommonToolbar = troRescoringInfo.toolbarConfiguration.toolbarType === TOOLBAR;

    const mergedEndPosition = isNil(troRescoringInfo.troRescoringData.endPosition)
        ? endPosition
        : troRescoringInfo.troRescoringData.endPosition;

    const isNotRead = isNil(startPosition) || isNil(mergedEndPosition);

    const getMarksClassNames = (marks, wordPosition, isEnd, isItalic) =>
        getTroMarksClassNames({
            marks,
            startPosition,
            endPosition: mergedEndPosition,
            wordPosition,
            isEnd,
            isItalic,
            isNotRead,
            activeWordPosition: troRescoringInfo.toolbarConfiguration.activeWordPosition,
            isInsertToolbar,
            isCommonToolbar,
            isEndTimeToolbar,
            isEditMode,
        });

    const getWidget = wordPosition => {
        const { activeWordPosition } = troRescoringInfo.toolbarConfiguration;

        return (
            wordPosition === activeWordPosition && (
                <RescoringToolbars
                    isCommonToolbar={isCommonToolbar}
                    isEndTimeToolbar={isEndTimeToolbar}
                    isInsertToolbar={isInsertToolbar}
                    toolbarConfiguration={troRescoringInfo.toolbarConfiguration}
                    corrections={troRescoringInfo.toolbarConfiguration.corrections}
                    audioDuration={audioDuration}
                    setWordEvent={setWordEvent}
                    onKeyDownHandler={onKeyDownHandler}
                    setEndWordData={setEndWordData}
                />
            )
        );
    };

    return (
        <Modal
            isPortal
            withOverlay
            withIcon={false}
            isShown={isEditMode}
            className="edit-mode-modal"
            modalHeader="EDIT MODE"
        >
            <section className="d-flex rescoring-edit-mode-wrapper">
                <aside className="left-side">
                    <h6 className="book-title">{bookTitle}</h6>
                    <div className="rescoring-content-wrapper editing">
                        <TroBookContent
                            isNotRead={isNotRead}
                            listOfSentences={sentences}
                            setEditMode={setEditMode}
                            startPosition={startPosition}
                            mergedEndPosition={mergedEndPosition}
                            getWidget={getWidget}
                            onPressHoldHandler={onPressHoldHandler}
                            onClickHandler={onClickHandler}
                            getMarksClassNames={getMarksClassNames}
                        />
                        <div className="banner">
                            <p className="banner-text">
                                {getTroBannerContent(isUntranscribable, complexityLevel)}
                            </p>
                            <Button
                                dataTestId="cancel-edit-mode-btn"
                                className="btn rescoring-secondary"
                                text="Cancel"
                                onClick={cancelEditMode}
                            />
                            <Button
                                dataTestId="save-edit-mode-btn"
                                isDisabled={troRescoringInfo.troRescoringData.isSaveButtonDisabled}
                                className="btn rescoring-primary"
                                text="Save"
                                onClick={saveEditMode}
                            />
                        </div>
                    </div>
                </aside>
                <aside className="right-side">
                    <AudioPlayer
                        audioSrc={audioSrc}
                        classNameWrapper="tro-audio-wrapper"
                        audioLookerEvent={audioLookerEvent}
                    />
                    <RescoringLegend legendItems={LEGEND_SECTION_EDIT_MODE} />
                    <RescoringInstruction
                        instructionType={
                            isUntranscribable ? NOT_SCORED_INSTRUCTION : COMMON_INSTRUCTION
                        }
                    />
                </aside>
            </section>
        </Modal>
    );
};

RescoringEditMode.defaultProps = {
    bookId: '',
    listOfSentences: [],
    vrrResult: {},
    bookInformation: {},
    isEditMode: false,
    setEditMode: () => {},
    closeConfirmationPopup: () => {},
    patchBookContentRemarks: () => {},
    audioLookerEvent: () => {},
};

RescoringEditMode.propTypes = {
    bookId: PropTypes.string,
    listOfSentences: PropTypes.array,
    vrrResult: PropTypes.object,
    bookInformation: PropTypes.object,
    isEditMode: PropTypes.bool,
    setEditMode: PropTypes.func,
    closeConfirmationPopup: PropTypes.func,
    patchBookContentRemarks: PropTypes.func,
    audioLookerEvent: PropTypes.func,
};

export default RescoringEditMode;
